/**
 * Risk-of-bias judgements as data: parsing a pasted table, the overall judgement, and
 * the shares behind a summary bar plot.
 *
 * The two tools and their judgement scales follow the published instruments:
 *
 * - **RoB 2** (Sterne et al., BMJ 2019;366:l4898) — five domains, each judged Low risk of
 *   bias, Some concerns or High risk of bias.
 * - **ROBINS-I** (Sterne et al., BMJ 2016;355:i4919) — seven domains, each judged Low,
 *   Moderate, Serious or Critical risk of bias, or No information.
 *
 * The plot layouts and the Cochrane and colour-blind palettes follow robvis
 * (McGuinness & Higgins, Research Synthesis Methods 2021;12(1):55–61): a traffic-light
 * plot of every judgement, and a stacked summary bar per domain, unweighted by default
 * and optionally weighted. robvis reads a judgement from its first letters; this reader
 * also takes the full words and the traffic-light symbols.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type RobToolId = "rob2" | "robinsi";

/**
 * A judgement on the scale both tools share. `mid` is "Some concerns" in RoB 2 and
 * "Moderate" in ROBINS-I; `high` is "High" and "Serious"; `critical` exists only in
 * ROBINS-I. Colours and symbols belong to the level, not to the tool's wording.
 */
export type RobLevel = "low" | "mid" | "high" | "critical" | "ni" | "na";

export type RobPaletteId = "cochrane" | "colourblind";

export interface RobTool {
  id: RobToolId;
  domainCount: number;
  /** Levels in plot order: how a summary bar is stacked from left to right. */
  levels: readonly RobLevel[];
}

export const ROB_TOOLS: readonly RobTool[] = [
  {
    id: "rob2",
    domainCount: 5,
    levels: ["low", "mid", "high", "ni", "na"],
  },
  {
    id: "robinsi",
    domainCount: 7,
    levels: ["low", "mid", "high", "critical", "ni", "na"],
  },
];

export function robTool(id: RobToolId): RobTool {
  return ROB_TOOLS.find((tool) => tool.id === id) ?? ROB_TOOLS[0];
}

/** robvis's palettes, hex for hex (`get_colour()` in the package source). */
export const ROB_PALETTES: Readonly<
  Record<RobPaletteId, Readonly<Record<RobLevel, string>>>
> = {
  cochrane: {
    low: "#02C100",
    mid: "#E2DF07",
    high: "#BF0000",
    critical: "#820000",
    ni: "#4EA1F7",
    na: "#CCCCCC",
  },
  colourblind: {
    low: "#FED98E",
    mid: "#FE9929",
    high: "#D95F0E",
    critical: "#993404",
    ni: "#FFFFFF",
    na: "#CCCCCC",
  },
};

/** The mark inside a traffic-light circle: + low, − mid, × high, ! critical, ? no information. */
export const ROB_SYMBOLS: Readonly<Record<RobLevel, string>> = {
  low: "+",
  mid: "−",
  high: "×",
  critical: "!",
  ni: "?",
  na: "",
};

const RE_HEX = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RE_PERIOD = /\./g;
const RE_SPACES = /\s+/g;
const RE_QUOTES = /^["“”']+|["“”']+$/g;
const RE_NEWLINE = /\r?\n/;

function channel(value: number): number {
  const unit = value / 255;
  return unit <= 0.039_28 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
}

function luminance(hex: string): number {
  const match = RE_HEX.exec(hex);
  if (!match) {
    return 0;
  }
  return (
    0.2126 * channel(Number.parseInt(match[1], 16)) +
    0.7152 * channel(Number.parseInt(match[2], 16)) +
    0.0722 * channel(Number.parseInt(match[3], 16))
  );
}

/** Black or white, whichever reads better on `fill` (WCAG contrast ratio). */
export function inkOn(fill: string): "#1f1f1f" | "#ffffff" {
  const light = luminance(fill) + 0.05;
  const darkInk = luminance("#1f1f1f") + 0.05;
  return light / darkInk >= 1.05 / light ? "#1f1f1f" : "#ffffff";
}

// ── Reading judgements ──────────────────────────────────────────────

const COMMON: Readonly<Record<string, RobLevel>> = {
  ni: "ni",
  "no information": "ni",
  "no info": "ni",
  "?": "ni",
  na: "na",
  "n/a": "na",
  "not applicable": "na",
};

const ROB2_WORDS: Readonly<Record<string, RobLevel>> = {
  ...COMMON,
  l: "low",
  low: "low",
  "low risk": "low",
  "low risk of bias": "low",
  "+": "low",
  s: "mid",
  sc: "mid",
  some: "mid",
  concern: "mid",
  concerns: "mid",
  "some concern": "mid",
  "some concerns": "mid",
  "-": "mid",
  "−": "mid",
  "–": "mid",
  h: "high",
  high: "high",
  "high risk": "high",
  "high risk of bias": "high",
  x: "high",
  "×": "high",
};

const ROBINSI_WORDS: Readonly<Record<string, RobLevel>> = {
  ...COMMON,
  l: "low",
  low: "low",
  "low risk": "low",
  "low risk of bias": "low",
  "+": "low",
  m: "mid",
  moderate: "mid",
  "moderate risk": "mid",
  "moderate risk of bias": "mid",
  "-": "mid",
  "−": "mid",
  "–": "mid",
  s: "high",
  serious: "high",
  "serious risk": "high",
  "serious risk of bias": "high",
  x: "high",
  "×": "high",
  c: "critical",
  critical: "critical",
  "critical risk": "critical",
  "critical risk of bias": "critical",
  "!": "critical",
};

/** A typed judgement as a level, or null if it is not one this tool uses. */
export function parseJudgement(tool: RobToolId, raw: string): RobLevel | null {
  const key = raw
    .trim()
    .toLowerCase()
    .replace(RE_QUOTES, "")
    .replace(RE_PERIOD, "")
    .replace(RE_SPACES, " ");
  const table = tool === "rob2" ? ROB2_WORDS : ROBINSI_WORDS;
  return Object.hasOwn(table, key) ? table[key] : null;
}

// ── Reading the pasted table ────────────────────────────────────────

export interface RobStudy {
  name: string;
  /** One level per domain, in domain order. */
  domains: RobLevel[];
  /** The overall judgement as entered, or null to derive it from the domains. */
  overall: RobLevel | null;
  /** The weight as entered, or null. */
  weight: number | null;
}

export type RobIssue =
  | { kind: "columns"; line: number; expected: number }
  | { kind: "judgement"; line: number; value: string }
  | { kind: "weight"; line: number; value: string };

export interface RobTable {
  studies: RobStudy[];
  issues: RobIssue[];
}

function splitRow(line: string): string[] {
  let delimiter = ",";
  if (line.includes("\t")) {
    delimiter = "\t";
  } else if (line.includes(";")) {
    delimiter = ";";
  }
  return line
    .split(delimiter)
    .map((cell) => cell.trim().replace(RE_QUOTES, ""));
}

/** The cells after the domains: an optional overall judgement, then an optional weight. */
function readTail(
  tool: RobToolId,
  tail: readonly string[],
  line: number
): { overall: RobLevel | null; weight: number | null; issue?: RobIssue } {
  const cells = tail.filter((cell) => cell !== "");
  let overall: RobLevel | null = null;
  let rest = cells;
  if (cells.length > 0) {
    const level = parseJudgement(tool, cells[0]);
    if (level !== null) {
      overall = level;
      rest = cells.slice(1);
    }
  }
  if (rest.length === 0) {
    return { overall, weight: null };
  }
  const weight = parseNumberInput(rest[0]);
  if (weight === null || weight < 0) {
    return {
      overall,
      weight: null,
      issue: { kind: "weight", line, value: rest[0] },
    };
  }
  return { overall, weight };
}

function readRow(
  tool: RobToolId,
  cells: readonly string[],
  line: number,
  domainCount: number
): { study?: RobStudy; issues: RobIssue[] } {
  if (cells.length < domainCount + 1) {
    return { issues: [{ kind: "columns", line, expected: domainCount }] };
  }
  const domains: RobLevel[] = [];
  const issues: RobIssue[] = [];
  for (const cell of cells.slice(1, domainCount + 1)) {
    const level = parseJudgement(tool, cell);
    if (level === null) {
      issues.push({ kind: "judgement", line, value: cell });
    } else {
      domains.push(level);
    }
  }
  const tail = readTail(tool, cells.slice(domainCount + 1), line);
  if (tail.issue) {
    issues.push(tail.issue);
  }
  if (issues.length > 0) {
    return { issues };
  }
  return {
    issues,
    study: {
      name: cells[0],
      domains,
      overall: tail.overall,
      weight: tail.weight,
    },
  };
}

/**
 * Rows of `Study, D1, …, Dn[, Overall][, Weight]`, separated by tabs, semicolons or
 * commas. A first row in which no cell after the study name is a judgement is taken as a header. A row
 * with an unreadable cell is left out and reported by line number.
 */
export function parseRobTable(tool: RobToolId, raw: string): RobTable {
  const { domainCount } = robTool(tool);
  const studies: RobStudy[] = [];
  const issues: RobIssue[] = [];
  const lines = raw.split(RE_NEWLINE);
  let first = true;
  for (const [index, text] of lines.entries()) {
    if (text.trim() === "") {
      continue;
    }
    const cells = splitRow(text);
    const header =
      first && !cells.slice(1).some((cell) => parseJudgement(tool, cell));
    first = false;
    if (header) {
      continue;
    }
    const row = readRow(tool, cells, index + 1, domainCount);
    issues.push(...row.issues);
    if (row.study) {
      studies.push(row.study);
    }
  }
  return { studies, issues };
}

// ── Overall judgement and summary shares ────────────────────────────

/**
 * The worst-domain rule: critical or high if any domain is, otherwise no information if
 * any domain lacks it, otherwise some concerns/moderate, otherwise low. It is the usual
 * reading of ROBINS-I; RoB 2 also lets assessors call "High" when several domains raise
 * some concerns, a judgement this rule cannot make — enter the overall column yourself
 * in that case.
 */
export function deriveOverall(domains: readonly RobLevel[]): RobLevel {
  for (const level of ["critical", "high", "ni", "mid", "low"] as const) {
    if (domains.includes(level)) {
      return level;
    }
  }
  return "na";
}

export function overallOf(study: RobStudy): RobLevel {
  return study.overall ?? deriveOverall(study.domains);
}

export interface SummarySegment {
  level: RobLevel;
  /** Share of the studies (or of the weight), 0–1. */
  share: number;
}

export interface SummaryBar {
  /** Domain index, or "overall". */
  domain: number | "overall";
  segments: SummarySegment[];
}

export function hasAllWeights(studies: readonly RobStudy[]): boolean {
  return (
    studies.length > 0 &&
    studies.every((study) => study.weight !== null && study.weight > 0)
  );
}

function barFor(
  levels: readonly RobLevel[],
  judged: readonly { level: RobLevel; weight: number }[],
  domain: number | "overall"
): SummaryBar {
  const total = judged.reduce((sum, item) => sum + item.weight, 0);
  const segments: SummarySegment[] = [];
  for (const level of levels) {
    const weight = judged
      .filter((item) => item.level === level)
      .reduce((sum, item) => sum + item.weight, 0);
    if (weight > 0 && total > 0) {
      segments.push({ level, share: weight / total });
    }
  }
  return { domain, segments };
}

/**
 * One stacked bar per domain, and one for the overall judgement. Unweighted, every
 * study counts once; weighted, each counts by its weight (the caller checks that every
 * study has one). Levels are stacked in the tool's order, so low risk sits at the left.
 */
export function summaryBars(
  tool: RobToolId,
  studies: readonly RobStudy[],
  { overall, weighted }: { overall: boolean; weighted: boolean }
): SummaryBar[] {
  const { domainCount, levels } = robTool(tool);
  const weightOf = (study: RobStudy) => (weighted ? (study.weight ?? 0) : 1);
  const bars: SummaryBar[] = [];
  for (let domain = 0; domain < domainCount; domain++) {
    bars.push(
      barFor(
        levels,
        studies.map((study) => ({
          level: study.domains[domain],
          weight: weightOf(study),
        })),
        domain
      )
    );
  }
  if (overall) {
    bars.push(
      barFor(
        levels,
        studies.map((study) => ({
          level: overallOf(study),
          weight: weightOf(study),
        })),
        "overall"
      )
    );
  }
  return bars;
}

/** The levels that occur anywhere in the plot, in the tool's order — what a legend lists. */
export function levelsUsed(
  tool: RobToolId,
  studies: readonly RobStudy[],
  overall: boolean
): RobLevel[] {
  const seen = new Set<RobLevel>();
  for (const study of studies) {
    for (const level of study.domains) {
      seen.add(level);
    }
    if (overall) {
      seen.add(overallOf(study));
    }
  }
  return robTool(tool).levels.filter((level) => seen.has(level));
}
